import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Heart, ArrowRight, Calendar } from "lucide-react";
import { Navigation } from "@/components/Navigation";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TribePage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/tribe/posts`);
        setPosts(response.data);
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-bone">
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-6 md:px-12 bg-[#F5F0EA]">
        <div className="max-w-screen-lg mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Heart size={24} className="text-sand-dark" fill="#A89A80" strokeWidth={1.5} />
              <p className="font-body text-xs tracking-[0.3em] uppercase text-sand-dark">
                Weekly Reflections
              </p>
            </div>
            
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight uppercase text-graphite-dark mb-4">
              My Tribe
            </h1>
            
            <p className="font-accent text-xl md:text-2xl italic text-graphite/70 mb-6">
              A Weekly Note from Elise
            </p>
            
            <p className="font-body text-base text-graphite/60 max-w-xl mx-auto leading-relaxed">
              Simple insights. Strong habits. A better tomorrow.
              <br />
              Each week, something thoughtful to strengthen your body, steady your mind, and inspire your next step.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Posts Grid */}
      <section className="py-16 md:py-24 px-6 md:px-12">
        <div className="max-w-screen-lg mx-auto">
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-pulse text-graphite/50">Loading...</div>
            </div>
          ) : posts.length === 0 ? (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-center py-20"
            >
              <Heart size={48} className="text-graphite/20 mx-auto mb-6" />
              <h2 className="font-heading text-2xl text-graphite-dark mb-4">Coming Soon</h2>
              <p className="font-body text-graphite/60 mb-8">
                The first weekly reflection is on its way.
              </p>
              <Link
                to="/#my-tribe"
                className="inline-flex items-center gap-2 px-6 py-3 bg-graphite-dark text-bone font-heading tracking-wider uppercase text-sm hover:bg-black transition-colors"
              >
                Subscribe to be notified
                <ArrowRight size={16} />
              </Link>
            </motion.div>
          ) : (
            <div className="space-y-12">
              {posts.map((post, index) => (
                <motion.article
                  key={post.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group"
                >
                  <Link to={`/tribe/${post.id}`} className="block">
                    <div className="bg-white border border-sand/20 p-8 md:p-12 hover:border-sand/40 transition-all duration-300">
                      {/* Featured Image */}
                      {post.featured_image && (
                        <div className="mb-8 -mx-8 md:-mx-12 -mt-8 md:-mt-12">
                          <img
                            src={post.featured_image}
                            alt={post.title}
                            className="w-full h-64 md:h-80 object-cover"
                          />
                        </div>
                      )}
                      
                      {/* Date */}
                      <div className="flex items-center gap-2 mb-4">
                        <Calendar size={14} className="text-sand-dark" />
                        <time className="font-body text-xs tracking-wider uppercase text-graphite/50">
                          {formatDate(post.published_at || post.created_at)}
                        </time>
                      </div>
                      
                      {/* Title */}
                      <h2 className="font-heading text-2xl md:text-3xl tracking-wide uppercase text-graphite-dark mb-3 group-hover:text-graphite transition-colors">
                        {post.title}
                      </h2>
                      
                      {/* Subtitle */}
                      {post.subtitle && (
                        <p className="font-accent text-lg italic text-graphite/60 mb-4">
                          {post.subtitle}
                        </p>
                      )}
                      
                      {/* Preview */}
                      <p className="font-body text-graphite/70 leading-relaxed line-clamp-3 mb-6">
                        {post.content.substring(0, 200)}...
                      </p>
                      
                      {/* Read More */}
                      <span className="inline-flex items-center gap-2 font-body text-sm tracking-wider uppercase text-sand-dark group-hover:text-graphite-dark transition-colors">
                        Read More
                        <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                      </span>
                    </div>
                  </Link>
                </motion.article>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Subscribe CTA */}
      <section className="py-16 px-6 md:px-12 bg-[#F5F0EA]">
        <div className="max-w-screen-md mx-auto text-center">
          <h3 className="font-heading text-2xl tracking-wide uppercase text-graphite-dark mb-4">
            Never Miss a Post
          </h3>
          <p className="font-body text-graphite/60 mb-8">
            Join My Tribe and receive weekly reflections directly to your inbox.
          </p>
          <Link
            to="/#my-tribe"
            className="inline-flex items-center gap-2 px-8 py-4 bg-graphite-dark text-bone font-heading tracking-wider uppercase text-sm hover:bg-black transition-colors"
          >
            Subscribe
            <Heart size={16} />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-graphite/5">
        <div className="max-w-screen-lg mx-auto text-center">
          <Link to="/" className="font-heading text-sm tracking-[0.2em] uppercase text-graphite/40 hover:text-graphite-dark transition-colors">
            Elise Redfield
          </Link>
        </div>
      </footer>
    </div>
  );
}
